import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { FaSync, FaDownload, FaTrash, FaChevronDown, FaChevronRight } from 'react-icons/fa';

import { getLogs, clearLogs, exportLogs, setLogLevel } from '../utils/logs';
import { useAdminAccess } from '../contexts/auth_context';

// ─── Constants ────────────────────────────────────────────────────────────────

const LEVEL_STYLES = {
	error: { bg: 'rgba(255,11,120,0.12)', color: '#FF0B78', border: 'rgba(255,11,120,0.3)' },
	warning: { bg: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: 'rgba(245,158,11,0.3)' },
	info: { bg: 'rgba(1,141,187,0.12)', color: '#018DBB', border: 'rgba(1,141,187,0.3)' },
	debug: {
		bg: 'var(--app-surface-muted)',
		color: 'var(--app-text-muted)',
		border: 'var(--app-border)',
	},
};

const LEVELS = ['error', 'warning', 'info', 'debug'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTimestamp = ts => {
	const d = new Date(ts);
	const date = d.toLocaleDateString('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
	const time = d.toLocaleTimeString('en-GB');
	return `${date}, ${time}`;
};

// ─── LogEntry ─────────────────────────────────────────────────────────────────

const LogEntry = ({ log }) => {
	const [dataOpen, setDataOpen] = useState(false);
	const [errOpen, setErrOpen] = useState(false);
	const s = LEVEL_STYLES[log.level] ?? LEVEL_STYLES.debug;

	return (
		<div
			className='px-5 py-3.5 transition-colors'
			style={{ borderBottom: '1px solid var(--app-border)' }}
			onMouseEnter={e => (e.currentTarget.style.background = 'var(--app-surface-muted)')}
			onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
		>
			<div className='flex items-start gap-3'>
				{/* Level badge */}
				<span
					className='flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border mt-0.5'
					style={{ background: s.bg, color: s.color, borderColor: s.border }}
				>
					{log.level.toUpperCase()}
				</span>

				{/* Message + meta */}
				<div className='flex-1 min-w-0'>
					<p
						className='text-[13px] font-medium m-0 leading-snug'
						style={{ color: 'var(--app-text)' }}
					>
						{log.message}
					</p>
					<p
						className='text-[11px] m-0 mt-0.5 font-mono'
						style={{ color: 'var(--app-text-muted)' }}
					>
						{formatTimestamp(log.timestamp)}
					</p>

					{/* Collapsible: additional data */}
					{log.data && (
						<div className='mt-2'>
							<button
								type='button'
								onClick={() => setDataOpen(v => !v)}
								className='flex items-center gap-1.5 text-[11px] font-medium transition-opacity hover:opacity-70'
								style={{
									color: '#018DBB',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									padding: 0,
								}}
							>
								{dataOpen ? (
									<FaChevronDown size={9} />
								) : (
									<FaChevronRight size={9} />
								)}
								Additional data
							</button>
							{dataOpen && (
								<pre
									className='text-[11px] mt-1.5 p-3 rounded-lg overflow-x-auto font-mono leading-relaxed'
									style={{
										background: 'var(--app-surface-muted)',
										color: 'var(--app-text)',
										border: '1px solid var(--app-border)',
									}}
								>
									{JSON.stringify(log.data, null, 2)}
								</pre>
							)}
						</div>
					)}

					{/* Collapsible: error details */}
					{log.error && (
						<div className='mt-2'>
							<button
								type='button'
								onClick={() => setErrOpen(v => !v)}
								className='flex items-center gap-1.5 text-[11px] font-medium transition-opacity hover:opacity-70'
								style={{
									color: '#FF0B78',
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									padding: 0,
								}}
							>
								{errOpen ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />}
								Error details
							</button>
							{errOpen && (
								<pre
									className='text-[11px] mt-1.5 p-3 rounded-lg overflow-x-auto font-mono leading-relaxed'
									style={{
										background: 'rgba(255,11,120,0.05)',
										color: '#FF0B78',
										border: '1px solid rgba(255,11,120,0.2)',
									}}
								>
									{JSON.stringify(log.error, null, 2)}
								</pre>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

// ─── LogsView ─────────────────────────────────────────────────────────────────

const LogsView = () => {
	const [logs, setLogs] = useState([]);
	const [filteredLogs, setFilteredLogs] = useState([]);
	const [filterLevel, setFilterLevel] = useState('');
	const [filterMessage, setFilterMessage] = useState('');
	const [currentLogLevel, setCurrentLogLevel] = useState('info');
	const { hasAdminAccess, isLoadingAccess } = useAdminAccess();

	const loadLogs = useCallback(() => setLogs(getLogs()), []);

	const filterLogs = useCallback(() => {
		let list = logs;
		if (filterLevel) list = list.filter(l => l.level === filterLevel);
		if (filterMessage)
			list = list.filter(l => l.message.toLowerCase().includes(filterMessage.toLowerCase()));
		setFilteredLogs(list);
	}, [logs, filterLevel, filterMessage]);

	useEffect(() => {
		loadLogs();
	}, [loadLogs]);
	useEffect(() => {
		filterLogs();
	}, [logs, filterLevel, filterMessage, filterLogs]);

	if (isLoadingAccess) {
		return (
			<div className='flex items-center justify-center min-h-[50vh]'>
				<div
					className='w-8 h-8 rounded-full border-2 animate-spin'
					style={{ borderColor: 'var(--app-border)', borderTopColor: '#FF0B78' }}
				/>
			</div>
		);
	}
	if (!hasAdminAccess) return <Navigate to='/' replace />;

	const handleClearLogs = () => {
		if (window.confirm('Clear all logs? This cannot be undone.')) {
			clearLogs();
			loadLogs();
		}
	};

	const handleExportLogs = () => {
		const blob = new Blob([exportLogs({ includeMetadata: true })], {
			type: 'application/json',
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `logs-${new Date().toISOString().split('T')[0]}.json`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const handleSetLogLevel = level => {
		setLogLevel(level);
		setCurrentLogLevel(level);
	};

	const inputStyle = {
		padding: '7px 12px',
		background: 'var(--app-input-bg)',
		border: '1px solid var(--app-border)',
		borderRadius: 8,
		fontSize: 12,
		color: 'var(--app-text)',
		fontFamily: 'inherit',
		outline: 'none',
		transition: 'border-color 0.15s',
	};

	return (
		<div
			className='min-h-[calc(100vh-96px)] p-6 md:p-8 pt-5 md:pt-6'
			style={{ color: 'var(--app-text)' }}
		>
			<div className='max-w-5xl mx-auto flex flex-col gap-5'>
				{/* ── Toolbar ───────────────────────────────────────────────── */}
				<div className='flex items-end gap-3 flex-wrap'>
					{/* Filters */}
					<div className='flex items-end gap-3 flex-1 flex-wrap'>
						<div className='flex flex-col gap-1'>
							<label
								htmlFor='log-level-filter'
								className='text-[10px] font-bold uppercase tracking-wider'
								style={{ color: 'var(--app-text-muted)' }}
							>
								Level filter
							</label>
							<select
								id='log-level-filter'
								value={filterLevel}
								onChange={e => setFilterLevel(e.target.value)}
								style={{ ...inputStyle, cursor: 'pointer' }}
								onFocus={e => (e.target.style.borderColor = '#FF0B78')}
								onBlur={e => (e.target.style.borderColor = 'var(--app-border)')}
							>
								<option value=''>All levels</option>
								{LEVELS.map(l => (
									<option key={l} value={l}>
										{l.charAt(0).toUpperCase() + l.slice(1)}
									</option>
								))}
							</select>
						</div>

						<div className='flex flex-col gap-1 flex-1' style={{ minWidth: 180 }}>
							<label
								htmlFor='log-message-filter'
								className='text-[10px] font-bold uppercase tracking-wider'
								style={{ color: 'var(--app-text-muted)' }}
							>
								Search
							</label>
							<input
								id='log-message-filter'
								type='text'
								value={filterMessage}
								onChange={e => setFilterMessage(e.target.value)}
								placeholder='Filter by message…'
								style={{ ...inputStyle, width: '100%' }}
								onFocus={e => (e.target.style.borderColor = '#FF0B78')}
								onBlur={e => (e.target.style.borderColor = 'var(--app-border)')}
							/>
						</div>

						<div className='flex flex-col gap-1'>
							<label
								htmlFor='log-level-setter'
								className='text-[10px] font-bold uppercase tracking-wider'
								style={{ color: 'var(--app-text-muted)' }}
							>
								Min log level
							</label>
							<select
								id='log-level-setter'
								value={currentLogLevel}
								onChange={e => handleSetLogLevel(e.target.value)}
								style={{ ...inputStyle, cursor: 'pointer' }}
								onFocus={e => (e.target.style.borderColor = '#FF0B78')}
								onBlur={e => (e.target.style.borderColor = 'var(--app-border)')}
							>
								{LEVELS.map(l => (
									<option key={l} value={l}>
										{l.charAt(0).toUpperCase() + l.slice(1)}
									</option>
								))}
							</select>
						</div>
					</div>

					{/* Action buttons */}
					<div className='flex items-center gap-2'>
						<button
							onClick={loadLogs}
							className='flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12px] font-medium border transition-all'
							style={{
								background: 'var(--app-surface-muted)',
								borderColor: 'var(--app-border)',
								color: 'var(--app-text-muted)',
							}}
						>
							<FaSync size={10} /> Refresh
						</button>
						<button
							onClick={handleExportLogs}
							className='flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12px] font-medium border transition-all'
							style={{
								background: 'var(--app-surface-muted)',
								borderColor: 'var(--app-border)',
								color: 'var(--app-text-muted)',
							}}
						>
							<FaDownload size={10} /> Export
						</button>
						<button
							onClick={handleClearLogs}
							className='flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12px] font-medium border transition-all'
							style={{
								background: 'rgba(255,11,120,0.06)',
								borderColor: 'rgba(255,11,120,0.2)',
								color: '#FF0B78',
							}}
							onMouseEnter={e =>
								(e.currentTarget.style.background = 'rgba(255,11,120,0.12)')
							}
							onMouseLeave={e =>
								(e.currentTarget.style.background = 'rgba(255,11,120,0.06)')
							}
						>
							<FaTrash size={10} /> Clear
						</button>
					</div>
				</div>

				{/* ── Stats strip ───────────────────────────────────────────── */}
				<div className='flex items-center gap-4'>
					<span className='text-[11px]' style={{ color: 'var(--app-text-muted)' }}>
						<span className='font-semibold' style={{ color: 'var(--app-text)' }}>
							{logs.length}
						</span>{' '}
						total
					</span>
					<span style={{ color: 'var(--app-border)' }}>·</span>
					<span className='text-[11px]' style={{ color: 'var(--app-text-muted)' }}>
						<span className='font-semibold' style={{ color: 'var(--app-text)' }}>
							{filteredLogs.length}
						</span>{' '}
						shown
					</span>
					{/* Level counts */}
					{LEVELS.map(lvl => {
						const count = logs.filter(l => l.level === lvl).length;
						if (!count) return null;
						const s = LEVEL_STYLES[lvl];
						return (
							<span key={lvl} className='flex items-center gap-1 text-[11px]'>
								<span
									className='w-1.5 h-1.5 rounded-full inline-block'
									style={{ background: s.color }}
								/>
								<span style={{ color: 'var(--app-text-muted)' }}>
									{count} {lvl}
								</span>
							</span>
						);
					})}
				</div>

				{/* ── Log list ──────────────────────────────────────────────── */}
				<div
					className='rounded-xl border overflow-hidden'
					style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
				>
					{/* Column header */}
					<div
						className='flex items-center gap-3 px-5 py-3 text-[11px] font-bold uppercase tracking-wider'
						style={{
							background: 'var(--app-surface-muted)',
							borderBottom: '1px solid var(--app-border)',
							color: 'var(--app-text-muted)',
						}}
					>
						<span className='w-16 flex-shrink-0'>Level</span>
						<span className='flex-1'>Message</span>
					</div>

					{filteredLogs.length === 0 ? (
						<div
							className='flex items-center justify-center py-16 text-[13px]'
							style={{ color: 'var(--app-text-muted)' }}
						>
							No logs found
						</div>
					) : (
						<div
							className='overflow-y-auto'
							style={{ maxHeight: 'calc(100vh - 320px)' }}
						>
							{filteredLogs.map((log, i) => (
								<LogEntry key={i} log={log} />
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default LogsView;
